import { isCalendarIsoDate } from './calendarDates';
import type { StayFilter } from './calendarHelpers';

export type CalendarMode = 'apartments' | 'parking' | 'resources';
export type CalendarZoom = 7 | 14 | 30 | 60;
export type CalendarDensity = 3 | 7 | 14;

/** Die neun URL-Parameter der Belegung; ihre Namen und Werte sind Teil geteilter Links. */
export interface CalendarSearch {
  focus?: string;
  from?: string;
  to?: string;
  zoom?: CalendarZoom;
  stay?: StayFilter;
  channels?: string;
  mode?: CalendarMode;
  density?: CalendarDensity;
  /** Der Tag des Ressourcen-Rasters; außerhalb des Zeitraums gilt heute bzw. der erste Tag. */
  day?: string;
}

const oneOf = <T extends string | number>(values: readonly T[], raw: unknown): T | undefined =>
  values.find((value) => String(value) === String(raw));

/**
 * Prüft die Adresse, bevor die Seite rechnet: ein leerer oder ungültiger Tag fällt weg und die
 * Seite zeigt heute, statt beim Rechnen mit „Invalid Date“ abzustürzen.
 */
export function validateCalendarSearch(search: Record<string, unknown>): CalendarSearch {
  return {
    focus: isCalendarIsoDate(search.focus) ? search.focus : undefined,
    // Ein frei gewählter Anzeigezeitraum; die Seite prüft Reihenfolge und Länge.
    from: isCalendarIsoDate(search.from) ? search.from : undefined,
    to: isCalendarIsoDate(search.to) ? search.to : undefined,
    zoom: oneOf([7, 14, 30, 60] as const, search.zoom),
    stay: oneOf(['all', 'current', 'future'] as const, search.stay),
    channels: typeof search.channels === 'string' ? search.channels : undefined,
    mode: oneOf(['parking', 'resources'] as const, search.mode),
    density: oneOf([3, 7, 14] as const, search.density),
    day: isCalendarIsoDate(search.day) ? search.day : undefined,
  };
}

/**
 * Stellplätze und Ressourcen sieht nur der Betrieb (die Endpunkte gehören Admin und Staff). Ein
 * geteilter Link fällt für alle anderen auf die Apartments zurück, statt in einem Ladefehler zu
 * enden.
 */
export function calendarModeFor(requested: CalendarMode | undefined, operations: boolean) {
  if (!requested || !operations) return 'apartments' as const;
  return requested;
}
